import { CalendarDays as TabIconCalendarDays, ArrowLeftRight as TabIconArrowLeftRight, FileJson as TabIconFileJson } from "lucide-react";
import { displayText } from "../../locales/display";
import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { Box, HBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Typo } from "@ui/text/Typo";
import { Signal } from "badmfck-signal";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAPI } from "src/hooks/useApi";
import { TClient, TReportTotal } from "../pages/ReportsPage";
import { S_CLIENT_CREDENTIALS_POPUP } from "../popup/ClientCredentialsPopup";
import { S_CLIENT_SETTINGS_POPUP } from "../popup/ClientSettingsPopup";
import { GD } from "src/GD";
import { ReportSessions } from "../forms/ReportSessions";
import { formatAmount } from "src/tools/tools";
import { notify } from "src/tools/notify";
import { useHasPermission } from "src/hooks/tools";

export const S_REPORT_POPUP = new Signal<{ id: number; date: string } | null>();
const REPORT_REGENERATE_ALLOWED_IDS = [1, 2, 22];
const TAB_DAILY_REPORT = 0;
const TAB_TRANSACTIONS = 1;
const TAB_RAW = 2;

const escapeCsv = (val: string | number | null | undefined) => {
	const s = String(val ?? "");
	return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (client: TClient): string => {
	const headers = [
		msg("client"),
		msg("merchant"),
		msg("channel"),
		msg("currency"),
		msg("net"),
		msg("fees_6d80486c"),
		msg("refunds_0942c179"),
		msg("chargebacks_655ad8a5"),
		msg("oct"),
		msg("rollings_bd316df6"),
		msg("releaseRollings"),
		msg("adjustmentIn"),
		msg("adjustmentOut"),
		msg("total"),
	];

	const rows: string[][] = [];

	for (const m of client.merchants) {
		for (const r of m.reports) {
			for (const [ccy, t] of Object.entries(r.total ?? {})) {
				rows.push([
					client.name,
					m.name,
					r.channel_name ?? r.channel_alias ?? "",
					ccy,
					t.total_net.toString(),
					t.total_fees.toString(),
					t.total_refunds.toString(),
					t.total_chargebacks.toString(),
					t.total_oct.toString(),
					t.total_rollings.toString(),
					t.total_release_rollings.toString(),
					t.total_ajustment_in.toString(),
					t.total_ajustment_out.toString(),
					t.total_amount.toString(),
				]);
			}
		}
	}

	return [headers.map(escapeCsv).join(","), ...rows.map(r => r.map(escapeCsv).join(","))].join("\r\n");
};

const downloadCsv = (client: TClient) => {
	const csv = buildCsv(client);
	const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
	saveAs(blob, `report_${client.name.replace(/\s+/g, "_")}_${client.id}.csv`);
};

export const ReportPopup = () => {
	useLocale();
	const signalData = S_REPORT_POPUP.use();
	const [report, reloadReport, busy, resetReport] = useAPI<TClient>(null);
	const [regenerateBusy, setRegenerateBusy] = useState(false);
	const [activeTab, setActiveTab] = useState(TAB_DAILY_REPORT);
	const transactionsCsvDownloadRef = useRef<() => void>(() => {});
	const [transactionsCsvState, setTransactionsCsvState] = useState({ busy: false, canDownload: false });
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const canViewReports = useHasPermission("reports.view");

	useEffect(() => {
		if (signalData && canViewReports) {
			reloadReport({
				method: "reports/get",
				data: {
					id: signalData.id,
				},
			});
		}
	}, [signalData, canViewReports]);

	const reportData = report && "data" in report ? (report.data as any) : null;
	//reportData && (reportData.clients[0].merchants = reportData.clients[0].merchants.concat(reportData.clients[0].merchants));

	const client: TClient = reportData?.data;
	//console.log("Report data", reportData);

	const closePopup = () => {
		S_REPORT_POPUP.invoke(null);
		resetReport();
		setActiveTab(TAB_DAILY_REPORT);
		transactionsCsvDownloadRef.current = () => {};
		setTransactionsCsvState({ busy: false, canDownload: false });
	};

	const updateTransactionsCsvState = useCallback((state: { busy: boolean; canDownload: boolean; download: () => void }) => {
		transactionsCsvDownloadRef.current = state.download;
		setTransactionsCsvState(current =>
			current.busy === state.busy && current.canDownload === state.canDownload ? current : { busy: state.busy, canDownload: state.canDownload },
		);
	}, []);

	if (!signalData || !canViewReports) return null;

	const canRegenerateReport = user && REPORT_REGENERATE_ALLOWED_IDS.includes(user.id);

	const regenerateReport = async () => {
		if (!signalData || regenerateBusy) return;

		setRegenerateBusy(true);
		const res = await GD.REQ_API.request({
			method: "gate/report/regenerate",
			data: {
				id: signalData.id,
			},
		});
		setRegenerateBusy(false);

		if (res?.error) {
			notify.error(res.error.message || msg("reportRegenerateFailed"));
			return;
		}

		notify.success(msg("reportRegenerated"));
		reloadReport({
			method: "reports/get",
			data: {
				id: signalData.id,
			},
		});
	};

	return (
		<Panel
			scrollable
			sx={{ width: "1100px", height: "90vh", backgroundColor: "#fff" }}
			decorator="absolute"
			label={<>{msg("reportDetails")}<DateTimeTooltip value={signalData.date}>{formatDisplayDate(signalData.date)}</DateTimeTooltip>)</>}
			onClose={() => {
				closePopup();
			}}>
			<ProgressBar busy={busy || regenerateBusy} />
			<Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "auto" }}>
				<Tabs variant="modern" scrollable onSectionSelect={setActiveTab}>
					<TabItem title={msg("dailyReport")} section={TAB_DAILY_REPORT} icon={<TabIconCalendarDays size={16} />}>
						{!client && !busy && (
							<Typo variant="caption" sx={{ opacity: 0.5, padding: "20px 0" }}>{msg("noReportDataAvailable")}</Typo>
						)}
						{client && <ReportTable client={client} date={reportData?.date} reportId={reportData?.id ?? signalData.id} />}
					</TabItem>
					<TabItem title={msg("transactions")} section={TAB_TRANSACTIONS} icon={<TabIconArrowLeftRight size={16} />}>
						<Box sx={{ height: "calc(90vh - 190px)", minHeight: 0, overflow: "hidden", display: "flex" }}>
							{reportData && (
								<ReportSessions
									id={reportData.id}
									reportDate={reportData.date ?? signalData.date}
									onCsvStateChange={updateTransactionsCsvState}
								/>
							)}
						</Box>
					</TabItem>
					<TabItem title={msg("raw_ac0562bb")} section={TAB_RAW} icon={<TabIconFileJson size={16} />}>
						<Pre data={report} />
					</TabItem>
				</Tabs>
			</Box>

			<Spacer />
			<HBox>
				{canRegenerateReport && (
					<Button
						variant="secondary"
						title={regenerateBusy ? msg("regenerating") : msg("regenerateReport")}
						disabled={regenerateBusy}
						onClick={regenerateReport}
					/>
				)}
				<Spacer grow />
				<Button
					variant="secondary"
					title={msg("downloadReportCsv")}
					onClick={() => {
						if (client) downloadCsv(client);
					}}
				/>
				{activeTab === TAB_TRANSACTIONS && (
					<>
						<Spacer size="10px" />
						<Button
							variant="secondary"
							title={transactionsCsvState.busy ? msg("downloading") : msg("downloadTransactionsCsv")}
							disabled={transactionsCsvState.busy || !transactionsCsvState.canDownload}
							onClick={() => transactionsCsvDownloadRef.current()}
						/>
					</>
				)}
			</HBox>
		</Panel>
	);
};

// ── Report table ──

const GRID_COLS = "4px 60px repeat(10, 1fr)";
const COL_HEADERS = () => [msg("ccy"), msg("turnover_8ecaf1d7"), "Adj. in", "Adj. out", msg("refunds_0942c179"), msg("chargebacks_655ad8a5"), msg("oct"), msg("rollings_bd316df6"), "Rel. rollings", msg("fees_6d80486c"), msg("net")];
const COL_COUNT = COL_HEADERS().length + 1; // stripe + data = 10

const cellBase: Record<string, string | number> = {
	padding: "5px 8px 5px 14px",
	whiteSpace: "nowrap",
	fontFamily: "monospace",
	fontSize: "14px",
};

const CLIENT_COLOR = "#34a853";
const CLIENT_BG = "#eef7f0";
const MERCHANT_COLOR = "#1a73e8";

const MERCHANT_BG = "#f0f6ff";

// const feeLabel: Record<string, string | number> = {
// 	fontSize: "11px",
// 	opacity: 0.55,
// 	marginRight: "4px",
// };
// const feeVal: Record<string, string | number> = {
// 	fontSize: "11px",
// 	marginRight: "12px",
// };

// /** Format commission line from channel rates */
// const formatCommission = (rates: any): string | null => {
// 	if (!rates?.data) return null;
// 	const d = rates.data;
// 	const fee = d.fee ?? 0;
// 	if (d.range && (d.fee_min || d.fee_max)) {
// 		return `${fee}% (min: ${d.fee_min} EUR, max: ${d.fee_max} EUR)`;
// 	}
// 	const fixed = d.fee_fixed ?? 0;
// 	return fixed ? `${fee}% + ${fixed} EUR` : `${fee}%`;
// };

/** Render fee info block for a single report */
// const ChannelFeesBlock = ({ report }: { report: any }) => {
// 	const fees = report.fees;
// 	if (!fees) return null;

// 	const rates = fees.channel?.rates;
// 	const setup = fees.client?.setup?.data;
// 	const rollings = fees.client?.rollings?.data;

// 	const commission = formatCommission(rates);
// 	const declineFee = rates?.data?.decline_fee;
// 	const refundFee = setup?.refund_fee;
// 	const chargebackFee = setup?.chargeback_fee;
// 	const reserved = rollings?.reserved;
// 	const period = rollings?.period;

// 	return (
// 		<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 0", marginTop: "4px" }}>
// 			{commission && (
// 				<>
// 					<Typo sx={feeLabel}>commission:</Typo>
// 					<Typo sx={feeVal}>{commission}</Typo>
// 				</>
// 			)}
// 			{declineFee != null && (
// 				<>
// 					<Typo sx={feeLabel}>decline:</Typo>
// 					<Typo sx={feeVal}>{declineFee} EUR</Typo>
// 				</>
// 			)}
// 			{refundFee != null && refundFee !== 0 && (
// 				<>
// 					<Typo sx={feeLabel}>refund:</Typo>
// 					<Typo sx={feeVal}>{refundFee} EUR</Typo>
// 				</>
// 			)}
// 			{chargebackFee != null && chargebackFee !== 0 && (
// 				<>
// 					<Typo sx={feeLabel}>chargeback:</Typo>
// 					<Typo sx={feeVal}>{chargebackFee} EUR</Typo>
// 				</>
// 			)}
// 			{reserved != null && (
// 				<>
// 					<Typo sx={feeLabel}>reserved:</Typo>
// 					<Typo sx={feeVal}>
// 						{reserved}%{period ? `, ${period} days` : ""}
// 					</Typo>
// 				</>
// 			)}
// 		</Box>
// 	);
// };

/** Empty spacer row spanning full grid width (but keeps client stripe) */
const SpacerRow = () => { useLocale(); return (<Box sx={{ gridColumn: `1 / ${COL_COUNT + 1}`, height: "12px" }} />); };

/** Table header row */
const GridHeader = ({ merchant, sticky }: { merchant?: boolean; sticky?: boolean }) => {
	useLocale();
	const stickyStyle = sticky ? { position: "sticky" as const, top: 0, zIndex: 1 } : {};
	return (
		<>
			<Box sx={{ backgroundColor: merchant ? MERCHANT_COLOR : "transparent", borderBottom: "2px solid #000", ...stickyStyle }} />
			{COL_HEADERS().map((h, i) => (
				<Box
					key={i}
					sx={{
						...cellBase,
						fontSize: "11px",
						fontWeight: 700,
						textTransform: "uppercase",
						letterSpacing: "0.03em",
						borderBottom: "2px solid #000",
						backgroundColor: merchant ? MERCHANT_BG : CLIENT_BG,
						...stickyStyle,
					}}>
					{displayText(h)}
				</Box>
			))}
		</>
	);
};

/** Data row */
const GridDataRow = ({ currency, total, merchant, bg }: { currency: string; total: TReportTotal; merchant?: boolean; bg?: string }) => {
	useLocale();
	const vals = [
		total.total_amount,
		total.total_ajustment_in,
		total.total_ajustment_out,
		total.total_refunds,
		total.total_chargebacks,
		total.total_oct,
		total.total_rollings,
		total.total_release_rollings,
		total.total_fees,
		total.total_net,
	];

	const mBg = merchant ? MERCHANT_BG : bg;
	const style = {
		...cellBase,
		backgroundColor: mBg,
		borderBottom: "1px solid #eee",
		// justifyContent: "end", display: "flex"
	};
	return (
		<>
			<Box sx={{ backgroundColor: merchant ? MERCHANT_COLOR : (bg ?? "transparent") }} />
			<Box sx={style}>{currency}</Box>
			{vals.map((v, i) => (
				<Box key={i} sx={style}>
					{formatAmount(v)}
				</Box>
			))}
		</>
	);
};

/** Info block spanning content columns */
const InfoRow = ({ children, padding, merchant, bg }: { children: React.ReactNode; padding?: string; merchant?: boolean; bg?: string }) => {
	useLocale();
	const rowBg = bg ?? (merchant ? MERCHANT_BG : "transparent");
	return (
		<>
			<Box sx={{ backgroundColor: merchant ? MERCHANT_COLOR : (bg ?? "transparent") }} />
			<Box
				sx={{
					gridColumn: `2 / ${COL_COUNT + 1}`,
					padding: padding ?? "12px 8px 6px",
					backgroundColor: rowBg,
				}}>
				{children}
			</Box>
		</>
	);
};

// ── Full report ──
const ReportTable = ({ client, date, reportId }: { client: TClient; date?: string; reportId?: number }) => {
	useLocale();
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const toggleMerchant = (key: string) => {
		setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
	};

	const openClientSettings = async () => {
		const res = await GD.REQ_CLIENT.request({
			clientID: client.id,
		});
		if (res?.data) {
			S_CLIENT_SETTINGS_POPUP.invoke({ client: res.data, section: "rolling", onUpdate: () => {} });
		}
	};

	const clientTotals = client.totals ? Object.entries(client.totals) : [];

	return (
		<Box sx={{ fontFamily: "monospace" }}>
			{reportId && <Typo sx={{ fontSize: "13px", opacity: 0.5, marginBottom: "6px" }}>{msg("reportId_449947db")}{reportId}</Typo>}
			{date && <Typo sx={{ fontSize: "13px", opacity: 0.5, marginBottom: "12px" }}>{msg("reportDate")}<DateTimeTooltip value={date}>{formatDisplayDate(date)}</DateTimeTooltip></Typo>}
			{client && (
				<Box key={client.id} sx={{ marginBottom: "32px" }}>
					{/* Sticky header: client info + column headers */}
					<Box
						sx={{
							position: "sticky",
							top: 0,
							zIndex: 2,
							display: "grid",
							gridTemplateColumns: GRID_COLS,
							backgroundColor: CLIENT_BG,
						}}>
						<InfoRow padding="4px 8px 10px" bg={CLIENT_BG}>
							<Typo
								sx={{ fontSize: "18px", color: CLIENT_COLOR, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
								onClick={() => {
									openClientSettings();
								}}>
								{client.name}
							</Typo>
							<Typo sx={{ fontFamily: "monospace", fontSize: "14px", opacity: 0.45, marginTop: "4px" }}>{msg("id")}{client.id}</Typo>
						</InfoRow>
						<GridHeader />
					</Box>

					{/* Data rows */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: GRID_COLS,
						}}>
						{/* Merchants */}
						{client.merchants?.map((m, mi) => {
							const mKey = `${m.id}_${mi}`;
							const isExpanded = !!expanded[mKey];
							const mTotals = m.totals ? Object.entries(m.totals) : [];
							return (
								<Box key={mKey} sx={{ display: "contents" }}>
									<SpacerRow />
									{/* Merchant info */}
									<InfoRow padding="4px 8px 10px" merchant>
										<HBox sx={{ alignItems: "start" }}>
											<Box>
												<Typo
													sx={{
														fontSize: "16px",
														color: MERCHANT_COLOR,
														cursor: "pointer",
														"&:hover": { textDecoration: "underline" },
													}}
													onClick={() => {
														//!TODO: clientID?
														S_CLIENT_CREDENTIALS_POPUP.invoke({
															merchantID: m.id,
															clienttID: m.client_id,
														});
													}}>
													{m.name}
												</Typo>
												<Typo sx={{ fontFamily: "monospace", fontSize: "13px", opacity: 0.45, marginTop: "4px" }}>
													ext: {m.merchant_external_id ?? "—"}
												</Typo>
												<Typo sx={{ fontFamily: "monospace", fontSize: "13px", opacity: 0.45, marginTop: "2px" }}>id: {m.id}</Typo>
											</Box>
											<Box
												sx={{
													marginLeft: "auto",
													cursor: "pointer",
													userSelect: "none",
													opacity: 0.4,
													padding: "8px 8px",
													display: "flex",
													alignItems: "center",
													borderRadius: "4px",
													transition: "opacity 0.15s ease, background-color 0.15s ease, transform 0.1s ease",
													"&:hover": { opacity: 1, backgroundColor: "rgba(26,115,232,0.12)" },
													"&:active": { transform: "scale(0.85)", backgroundColor: "rgba(26,115,232,0.22)" },
												}}
												onClick={() => toggleMerchant(mKey)}>
												<svg
													width="12"
													height="12"
													viewBox="0 0 12 12"
													style={{
														transform: isExpanded ? "rotate(-90deg)" : "rotate(90deg)",
														transition: "transform 0.15s ease",
													}}>
													<path
														d="M4 1L9 6L4 11"
														stroke="currentColor"
														strokeWidth="2"
														strokeLinecap="round"
														strokeLinejoin="round"
														fill="none"
													/>
												</svg>
											</Box>
										</HBox>
									</InfoRow>

									{/* Collapsed: merchant totals */}
									<Box
										sx={{
											gridColumn: "1 / -1",
											display: "grid",
											gridTemplateRows: !isExpanded ? "1fr" : "0fr",
											transition: "grid-template-rows 0.3s ease",
										}}>
										<Box sx={{ overflow: "hidden", display: "grid", gridTemplateColumns: GRID_COLS }}>
											{mTotals.map(([ccy, t]) => (
												<GridDataRow key={`mt_${ccy}`} currency={ccy} total={t} merchant bg="#e8f0fe" />
											))}
										</Box>
									</Box>

									{/* Expanded: channel breakdown */}
									<Box
										sx={{
											gridColumn: "1 / -1",
											display: "grid",
											gridTemplateRows: isExpanded ? "1fr" : "0fr",
											transition: "grid-template-rows 0.3s ease",
										}}>
										<Box sx={{ overflow: "hidden", display: "grid", gridTemplateColumns: GRID_COLS }}>
											{m.reports.map((r, ri) => {
												const channelTotals = r.total ? Object.entries(r.total) : [];
												return (
													<Box key={`ch_${ri}`} sx={{ display: "contents" }}>
														<InfoRow padding="10px 8px 2px 8px" merchant>
															<Typo
																sx={{
																	fontSize: "12px",
																	opacity: 0.55,
																	textTransform: "uppercase",
																	letterSpacing: "0.04em",
																	cursor: "pointer",
																	"&:hover": { opacity: 1, textDecoration: "underline" },
																}}
																onClick={() => {
																	//!TODO: clientID?
																	S_CLIENT_CREDENTIALS_POPUP.invoke({
																		merchantID: m.id,
																		clienttID: m.client_id,
																		tab: 3,
																		channelId: r.channel_id,
																	});
																}}>
																{r.channel_name ?? r.channel_alias ?? "—"}
																{r.channel_name && r.channel_alias ? ` (${r.channel_alias})` : ""}
															</Typo>
														</InfoRow>
														{channelTotals.map(([ccy, t]) => (
															<GridDataRow key={`chr_${ri}_${ccy}`} currency={ccy} total={t} merchant />
														))}
													</Box>
												);
											})}
										</Box>
									</Box>
								</Box>
							);
						})}

						{/* Client totals (summary at bottom) */}
						{clientTotals.length > 0 && (
							<>
								<SpacerRow />
								<InfoRow padding="4px 8px 2px" bg={CLIENT_BG}>
									<Typo
										sx={{
											fontSize: "13px",
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
											color: CLIENT_COLOR,
										}}>{msg("total")}</Typo>
								</InfoRow>
								{clientTotals.map(([ccy, t]) => (
									<GridDataRow key={`ct_${ccy}`} currency={ccy} total={t} bg={CLIENT_BG} />
								))}
							</>
						)}
					</Box>
				</Box>
			)}
		</Box>
	);
};
